import { AnalyticsEventLocation } from './analytics';
import type { Project, AddToCartResponse } from '../types';
import { CustomerProfileAddress } from './addresses';

export enum CustomEventName {
  OPEN_PROJECT_SELECTION_DIALOG = 'open-project-selection-dialog',
  OPEN_ASK_PRODUCT_QUESTION_DIALOG = 'open-ask-product-question-dialog',
  OPEN_ASSOCIATED_PRODUCTS_MODAL = 'open-associated-finishes-products-modal',
  SHOW_CONTROL_RULES_MODAL = 'show-control-rules-modal',
  RELOAD_CUSTOMER_SECTION_DATA = 'reload-customer-section-data',
  PRIVATE_CONTENT_LOADED = 'private-content-loaded',
  OPEN_CREATE_PROJECT_MODAL = 'open-create-project-modal',
  OPEN_CONTACT_REP_MODAL = 'open-contact-rep-modal',
  OPEN_CONTACT_REP_MODAL_V2 = 'open-contact-rep-modal-v2',
  OPEN_CONTACT_REP_MODAL_V3 = 'open-contact-rep-modal-v3',
  SHOW_COLLECTION_HEADER = 'show-collection-header',
  LOADED_COLLECTION_HEADER = 'loaded-collection-header',
  LOADED_SEARCH_CONTEXT = 'loaded-search-context',
  OPEN_USER_PROFILE_DIALOG = 'open-user-profile-dialog',
  OPEN_CUSTOMER_MENU_INBOX = 'customer-menu-inbox',
  OPEN_ADDRESSES_MODAL = 'address-modal',
  UPDATE_MULTI_ADDRESSES = 'update-multi-addresses',
  UPDATE_DEFAULT_SHIPPING = 'update-default-shipping',
  UPDATE_ACTIVE_BRAND = 'active-brand',
  CUSTOMER_MENU_PROFILE_EDIT = 'customer-menu-profile-edit',
  OPEN_PROJECTS_LIST = 'open-projects-list',
  ARCHIVE_PROJECT = 'archive-project',
  OPEN_GDPR_DIALOG = 'open-gdpr',
  FIRM_MENU_TOGGLE = 'firm-menu-toggle',
  OPEN_ADDRESS_FORM = 'address-form',
  OPEN_BOOK_MEETING_DIALOG = 'open-book-meeting-dialog',
}

export interface UpdateDefaultShippingEventDetailData {
  addressId: string;
}

export interface UpdateMultiAddressesEventDetailData {
  multiAddressesList: CustomerProfileAddress[];
}

export interface UpdateActiveBrandEventDetailData {
  activeBrand: {
    id: string;
    manufacturer_id: string;
    manufacturer_name: string;
    default?: boolean;
    rep_tools?: string;
  };
}

export interface AddressesModalEventData extends Event {
  detail?: {
    multiAddress?: boolean;
    multiAddressesList?: string[];
    selectedAddressId?: number;
  };
}

export const reloadCustomerSectionData = () => {
  window.dispatchEvent(
    new CustomEvent(CustomEventName.RELOAD_CUSTOMER_SECTION_DATA),
  );
};

export const triggerAskProductQuestionDialog = () => {
  window.dispatchEvent(
    new CustomEvent(CustomEventName.OPEN_ASK_PRODUCT_QUESTION_DIALOG),
  );
};

export type EventDetails = {
  currentProjectId: number;
  analyticsEventLocation: AnalyticsEventLocation;
  onCurrentProjectSave?: (project: Project) => Promise<void>;
  onProjectCreate?: (project: Project) => void;
};

export const triggerProjectSelectionDialog = (eventDetails: EventDetails) => {
  window.dispatchEvent(
    new CustomEvent(CustomEventName.OPEN_PROJECT_SELECTION_DIALOG, {
      detail: eventDetails,
    }),
  );
};

export const triggerAssociatedProductsModal = (
  products: string[],
  data: {
    id: number;
    name: string;
    manufacturerName: string;
    productImageUrl: string;
  },
) => {
  window.dispatchEvent(
    new CustomEvent(CustomEventName.OPEN_ASSOCIATED_PRODUCTS_MODAL, {
      detail: {
        associatedProducts: products.join('|'),
        parentProduct: data,
      },
    }),
  );
};

export const triggerCreateNewProjectModal = () => {
  window?.dispatchEvent(
    new CustomEvent(CustomEventName.OPEN_CREATE_PROJECT_MODAL),
  );
};

export const triggerControlRulesModal = (data: AddToCartResponse) => {
  window.dispatchEvent(
    new CustomEvent(CustomEventName.SHOW_CONTROL_RULES_MODAL, {
      detail: {
        data,
      },
    }),
  );
};

export const triggerUpdateMultiAddresses = (
  data: UpdateMultiAddressesEventDetailData,
) => {
  window.dispatchEvent(
    new CustomEvent(CustomEventName.UPDATE_MULTI_ADDRESSES, {
      detail: data,
    }),
  );
};

export const triggerUpdateDefaultShipping = (
  data: UpdateDefaultShippingEventDetailData,
) => {
  window.dispatchEvent(
    new CustomEvent(CustomEventName.UPDATE_DEFAULT_SHIPPING, {
      detail: data,
    }),
  );
};

export type ContactRepModalData = {
  event_location: string;
  product_id: string;
  product_name: string;
  project_id: string;
  manufacturer_sku: string;
  manufacturer: string;
  manufacturer_id?: string;
  type?: string;
};

export type ContactRepModalDetails = {
  modalPlacement: 'top' | 'bottom';
  portalTarget: HTMLElement;
  data: ContactRepModalData;
};

export const triggerContactRepModal = (
  eventDetails: ContactRepModalDetails,
) => {
  window.dispatchEvent(
    new CustomEvent(CustomEventName.OPEN_CONTACT_REP_MODAL_V3, {
      detail: eventDetails.data,
    }),
  );
};

export const openCustomerMenuInbox = () => {
  window.dispatchEvent(
    new CustomEvent(CustomEventName.OPEN_CUSTOMER_MENU_INBOX, {
      detail: true,
    }),
  );
};

export const triggerUpdateActiveBrand = (
  eventDetail: UpdateActiveBrandEventDetailData,
) => {
  window.dispatchEvent(
    new CustomEvent(CustomEventName.UPDATE_ACTIVE_BRAND, {
      detail: eventDetail,
    }),
  );
};

export const openGdprDialog = () => {
  window.dispatchEvent(new CustomEvent(CustomEventName.OPEN_GDPR_DIALOG));
};
